/**
 * A reply that failed to send, as the transcript's banner needs to DESCRIBE it and not merely locate
 * it (CONAPP-367).
 *
 * ⚠ THE STATUS IS PART OF THE SIGNAL. Keyed on the thread alone this channel could only ever render
 * the one sentence it always has — "try again" — which is the wrong advice for the one failure a
 * member can act on: a file the transport refuses for what it IS (a type off the allowlist, bytes
 * that do not match the declared type, a deployment with attachments turned off) is refused
 * identically on every retry, so "try again" loops forever. `null` is the ordinary shape of a torn
 * socket or an aborted fetch, which reaches the package with no upstream status at all — see
 * `withStatusCode` in the host adapter.
 *
 * ⚠ AND `hadAttachment` CANNOT BE DERIVED FROM THE STATUS. A 400-class refusal of a TEXT-only send
 * is a real case with a test of its own, and the file copy would be nonsense for it. Only the
 * composer knows what the send carried.
 */
export type SendFailure = {
  statusCode: number | null
  hadAttachment: boolean
}

/**
 * Which conversation's last reply FAILED to send, held above the composer and above the panel
 * (CONAPP-559).
 *
 * ⛔ IT IS HERE FOR THE SAME REASON `draftsByThread`, `sends` AND `picks` ARE — LIFETIME. A send
 * outlives every component that could hold its verdict: the member selects another conversation
 * while it is out, or one of the three unmounts takes the panel with the panel-scoped context in it
 * (the closed-conversation swap, the Companion tab, the list poll exhausting its retries). The
 * failure then landed in an unmounted provider's setter and the member was never told at all.
 *
 * ⛔ AND THAT IS WORST ON A `file-refused` 400. The composer has already taken the chip off by the
 * time it records this, so a member coming back to their restored words met no chip, no refusal
 * notice and no banner — a composer that looks ready — and the next tap posted the note without the
 * file. `createNote` has no idempotency key, so the note with the file missing is permanent.
 *
 * ⛔ OBSERVABLE RATHER THAN A BARE MAP, which is where it differs from `draftsByThread`: the banner
 * RENDERS from it, and the send whose failure has to reach the member settles inside a closure
 * belonging to an unmounted composer — so the transcript on screen learns about it only by
 * subscribing. This is a `useSyncExternalStore` store, and `announce` is what reaches the live
 * instance. Same shape, and the same argument, as `MessageSendStore` and `MessagePickStore`.
 *
 * ⚠ PER CONVERSATION, unlike the send gate's `isSending`. A failure is a verdict on one clinician's
 * note, and the banner renders inside that conversation's transcript.
 *
 * ⚠ MOUNT-SCOPED, NEVER A MODULE CONSTANT. A status code is not clinical content, but an entry here
 * still says a signed-in member tried to write to a named conversation — so the host builds one per
 * `MessagesShell` mount (see src/pages/messages-shell.tsx) and nothing here survives a sign-out.
 */
export type MessageFailureStore = {
  /** React's `useSyncExternalStore` subscription. Returns the unsubscribe. */
  subscribe: (onStoreChange: () => void) => () => void
  /**
   * How this conversation's last reply failed, or null.
   *
   * ⚠ RETURNS THE STORED OBJECT ITSELF, because `useSyncExternalStore` compares snapshots with
   * `Object.is` — a getter building a fresh value would re-render forever.
   */
  failure: (threadId: string) => SendFailure | null
  /** This conversation's reply did not reach the transport. Displaces whatever it was holding. */
  fail: (threadId: string, failure: SendFailure) => void
  /**
   * Takes this conversation's banner down.
   *
   * ⛔ CALLED ON A POST, AND WHEN THE MEMBER LEAVES THE CONVERSATION THE BANNER IS SHOWING IN — never
   * merely because some other conversation was selected. The difference is the whole point of the
   * store: a failure the member has not been shown yet has to survive until they are standing where
   * it renders (see `InAppMessagingContextProvider`).
   */
  clear: (threadId: string) => void
}

export function createMessageFailureStore(): MessageFailureStore {
  const failures = new Map<string, SendFailure>()
  const listeners = new Set<() => void>()

  function announce(): void {
    listeners.forEach((listener) => listener())
  }

  return {
    subscribe: (onStoreChange) => {
      listeners.add(onStoreChange)
      return () => {
        listeners.delete(onStoreChange)
      }
    },
    failure: (threadId) => failures.get(threadId) ?? null,
    fail: (threadId, failure) => {
      failures.set(threadId, failure)
      announce()
    },
    clear: (threadId) => {
      // ⚠ ONLY WHEN THERE WAS ONE. Every selection change asks this of the conversation being left,
      // and announcing regardless would re-render the transcript on every move for nothing.
      if (failures.delete(threadId)) announce()
    }
  }
}
